import { motion } from "framer-motion";
import { PageMeta } from "../seo/PageMeta";
import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";


function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = [
    { label: "Accueil", href: "#home" },
    { label: "Nous contacter", href: "/contact" },
    { label: "À propos", href: "/about" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-0 transition-all duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto w-full max-w-6xl py-4 md:py-5">
        <div className="flex items-center justify-between md:hidden">
          <a href="#home" className="text-sm font-semibold tracking-wide text-[#1a1a2e]">
            LMS
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Ouvrir le menu"
            className="rounded-lg p-2 text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
          >
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current" />
          </button>
        </div>

        <div className="hidden md:flex justify-center gap-10">
          {navLinks.map(({ label, href }) => (
            <motion.a
              key={label}
              href={href}
              className="relative text-sm font-medium text-[#1a1a2e] tracking-wide"
              whileHover="hover"
              animate="rest"
            >
              {label}
              <motion.span
                className="absolute -bottom-0.5 left-0 h-[1.5px] bg-[#1a1a2e] block"
                variants={{
                  rest: { width: "0%" },
                  hover: { width: "100%" },
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </motion.a>
          ))}
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="mt-3 rounded-2xl border border-[#1a1a2e]/10 bg-white/95 p-3 shadow-md md:hidden"
            >
              <div className="flex flex-col">
                {navLinks.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}

function NotFoundFooter() {
  return (
    <footer className="bg-[#0C1925] px-6 py-6 text-white/80" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-4 text-sm md:flex-row">
        <p className="text-center text-white/50 md:text-left">
          © {new Date().getFullYear()} LMS Technologies. Tous droits réservés.
        </p>
        <div className="flex w-full items-center justify-center gap-5 text-center lg:w-auto lg:justify-start lg:text-left">
          <a href="/condition" className="text-white/60 transition-colors hover:text-white">
            Conditions d&apos;utilisation
          </a>
          <span className="text-white/25">|</span>
          <a href="/confidentiality" className="text-white/60 transition-colors hover:text-white">
            Politique de confidentialité
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function NotFound() {
  return (
    <>
      <PageMeta
        title="Page introuvable"
        description="Cette adresse ne correspond à aucune page sur le site LMS Technologies."
        path=""
        robots="noindex, nofollow"
        omitCanonical
      />
      <div
        className="flex min-h-screen flex-col"
        style={{
          fontFamily: "'Inter', sans-serif",
          background: "linear-gradient(160deg,rgb(253, 253, 253) 0%, #C9E5FF 100%)",
        }}
      >
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-28 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-[#4F9ECF]">Erreur 404</p>
            <h1 className="mt-3 text-5xl font-semibold text-[#0C1925] md:text-7xl">404</h1>
            <p className="mx-auto mt-4 max-w-md text-lg text-[#1a1a2e]/70">
              La page que vous cherchez n&apos;existe pas ou n&apos;est plus disponible.
            </p>
            <a
              href="/"
              className="mt-8 inline-flex rounded-full bg-[#0C1925] px-8 py-3 text-base font-medium text-white shadow-sm transition-shadow hover:shadow-md"
            >
              Retour à l&apos;accueil
            </a>
          </motion.div>
        </main>
        <NotFoundFooter />
      </div>
    </>
  );
}
